import React, { useEffect, useRef, useState } from "react";
import { CategoryOption } from "../categoryFilter";
import CraftWeavePanel, { CraftLoaders } from "./CraftWeavePanel";
import PriceFilterBlock, { priceHint } from "./PriceFilterBlock";
import { COLOUR_SWATCHES, COMMON_COLOURS } from "./colourSwatches";
import {
  ADDED_PRESETS,
  AddedPreset,
  CRAFT_LEVELS,
  ProductListFilters,
  applyStatusTab,
  statusTabFor,
} from "./productListFilters";
import { PlIcon } from "./plIcons";
import TypeTagFilterSection from "./TypeTagFilterSection";

export { COLOUR_SWATCHES };

export type FilterSection = "category" | "price" | "stock" | "colour" | "added" | "craft" | "transferred" | "ids" | "describe" | "types";

const CATEGORY_PREVIEW_PANE = 5;
const CATEGORY_PREVIEW_SHEET = 8;

type Props = {
  mode: "sheet" | "pane";
  filters: ProductListFilters;
  /** Apply a change; `typing` waits for the user to stop typing before fetching. */
  onChange: (next: ProductListFilters, opts?: { typing?: boolean }) => void;
  categoryOptions: CategoryOption[];
  priceFiltersEnabled: boolean;
  draftRevisionsEnabled: boolean;
  loaders: CraftLoaders;
  focusSection?: FilterSection | null;
  /** Show just this section (a phone chip like "Price" opens only Price); null shows everything. */
  only?: FilterSection | null;
  /** Sheet only: open the craft & weave drill-in. */
  onOpenCraft?: () => void;
};

/** Collapsed row that opens in place: "Craft & weave · artform → weave ›" (pane) or an icon row (sheet). */
const Disclosure = ({
  id,
  title,
  hint,
  open,
  onToggle,
  icon,
  mode,
  children,
}: {
  id: string;
  title: string;
  hint: string;
  open: boolean;
  onToggle: () => void;
  icon: string;
  mode: "sheet" | "pane";
  children?: React.ReactNode;
}) => (
  <div className={`pl-disclosure${open ? " is-open" : ""}`} data-section={id}>
    <button type="button" className="pl-disclosure__head" aria-expanded={children ? open : undefined} onClick={onToggle}>
      {mode === "sheet" ? <PlIcon name={icon} /> : null}
      {mode === "sheet" ? (
        <span className="pl-disclosure__text">
          <b>{title}</b>
          <small>{hint}</small>
        </span>
      ) : (
        <span className="pl-disclosure__text is-inline">
          <b>{title}</b>
          <small> · {hint}</small>
        </span>
      )}
      <PlIcon name="right" size={16} className="pl-disclosure__chev" />
    </button>
    {open && children ? <div className="pl-disclosure__body">{children}</div> : null}
  </div>
);

const Switch = ({ on, label, onToggle }: { on: boolean; label: string; onToggle: () => void }) => (
  <button type="button" role="switch" aria-checked={on} aria-label={label} className={`pl-switch${on ? " is-on" : ""}`} onClick={onToggle}>
    <span className="pl-switch__knob" />
  </button>
);

/**
 * Filters in the approved order: Category, Selling price, Stock (+ "Live with edits not published"),
 * Colour, Added, then Craft & weave, Transferred, Exact IDs & tags and Describe it as rows that open.
 * Every change applies at once, so the list and the "Show N products" count stay live.
 */
const ProductFiltersPanel = ({
  mode,
  filters,
  onChange,
  categoryOptions,
  priceFiltersEnabled,
  draftRevisionsEnabled,
  loaders,
  focusSection,
  only = null,
  onOpenCraft,
}: Props) => {
  const hide = (section: FilterSection) => Boolean(only) && only !== section;
  const pane = mode === "pane";
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [allCategories, setAllCategories] = useState(false);
  const craftCount = CRAFT_LEVELS.filter((l) => (filters[l] as any)?.value && !(l === "category" && (filters.category as any)?.byName)).length;
  const idsCount = [filters.xuid, filters.ProductId, filters.tags].filter(Boolean).length;
  const colour = (filters.color || "").trim().toLowerCase();
  const commonColours = COLOUR_SWATCHES.slice(0, pane ? COMMON_COLOURS : COMMON_COLOURS + 1);
  const [moreColours, setMoreColours] = useState(() => Boolean(colour) && !commonColours.some(([n]) => n.toLowerCase() === colour));
  const [open, setOpen] = useState<Record<string, boolean>>(() => ({
    craft: craftCount > 0,
    transferred: filters.view === "transferredIn" || filters.view === "transferredOut",
    ids: idsCount > 0,
    describe: Boolean(filters.q) && filters.searchScope !== "all",
  }));
  const toggle = (key: string) => setOpen((prev) => ({ ...prev, [key]: !prev[key] }));

  useEffect(() => {
    if (!focusSection) return;
    if (["craft", "transferred", "ids", "describe"].includes(focusSection)) {
      setOpen((prev) => ({ ...prev, [focusSection]: true }));
    }
    const el = rootRef.current?.querySelector(`[data-section="${focusSection}"]`) as HTMLElement | null;
    el?.scrollIntoView?.({ block: "start" });
  }, [focusSection]);

  const category = filters.category && (filters.category as CategoryOption).value ? (filters.category as CategoryOption) : null;
  const selectedCategoryName = category ? category.label.toLowerCase() : "";
  const preview = pane ? CATEGORY_PREVIEW_PANE : CATEGORY_PREVIEW_SHEET;
  const shownCategories = (allCategories ? categoryOptions : categoryOptions.slice(0, preview)).slice();
  if (category && !shownCategories.some((o) => o.label.toLowerCase() === selectedCategoryName)) {
    shownCategories.unshift({ label: category.label, value: category.value, byName: category.byName });
  }
  const pickCategory = (option: CategoryOption) => {
    const on = option.label.toLowerCase() === selectedCategoryName;
    onChange(
      on
        ? { ...filters, category: "", subCategory: "", weaveType: "" }
        : { ...filters, category: { label: option.label, value: option.label, byName: true }, subCategory: "", weaveType: "" }
    );
  };
  const stockTab = statusTabFor(filters);
  const added = filters.added || { preset: "any" as AddedPreset };
  const addedPresets = ADDED_PRESETS.filter((p) => !pane || p.key !== "7d" || added.preset === "7d");
  const transferred = filters.view === "transferredIn" || filters.view === "transferredOut" ? filters.view : "";
  const H = ({ title, hint }: { title: string; hint?: React.ReactNode }) => (
    <div className="pl-fgrp__h">
      {/* With `only`, the sheet title already names the section. */}
      <span>{only ? "" : title}</span>
      {hint ? <i>{hint}</i> : null}
    </div>
  );

  const shownColours = moreColours ? COLOUR_SWATCHES : commonColours;

  return (
    <div className={`pl-filters is-${mode}`} ref={rootRef}>
      <section className="pl-fgrp" data-section="category" hidden={hide("category")} aria-label="Category">
        <H title="Category" hint={pane ? (category ? "1" : categoryOptions.length ? String(categoryOptions.length) : "") : "most used first"} />
        {pane ? (
          <div className="pl-cklist" role="radiogroup" aria-label="Category">
            {shownCategories.map((option) => {
              const on = option.label.toLowerCase() === selectedCategoryName;
              return (
                <button
                  key={`${option.label}-${option.value}`}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  className="pl-ck"
                  onClick={() => pickCategory(option)}
                >
                  <span className={`pl-ck__box${on ? " is-on" : ""}`} aria-hidden="true" />
                  <span className="pl-grow pl-ellipsis">{option.label}</span>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="pl-chips is-wrap">
            {shownCategories.map((option) => {
              const on = option.label.toLowerCase() === selectedCategoryName;
              return (
                <button
                  key={`${option.label}-${option.value}`}
                  type="button"
                  aria-pressed={on}
                  className={`pl-chip${on ? " is-on" : ""}`}
                  onClick={() => pickCategory(option)}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        )}
        {categoryOptions.length > preview ? (
          <button type="button" className="pl-link pl-link--start" onClick={() => setAllCategories((v) => !v)}>
            {allCategories ? "Show fewer" : `Show all ${categoryOptions.length}`}
          </button>
        ) : null}
        {categoryOptions.length === 0 ? <span className="pl-note">No categories yet.</span> : null}
      </section>

      {priceFiltersEnabled ? (
        <section className="pl-fgrp" data-section="price" hidden={hide("price")} aria-label="Selling price">
          <H title="Selling price" hint={priceHint(filters.price)} />
          <PriceFilterBlock value={filters.price || {}} onChange={(value) => onChange({ ...filters, price: value })} compact={pane} />
        </section>
      ) : null}

      <section className="pl-fgrp" data-section="stock" hidden={hide("stock")} aria-label="Stock">
        <H title="Stock" />
        <div className="pl-seg" role="radiogroup" aria-label="Stock">
          {([
            ["all", "All"],
            ["inStock", pane ? "In" : "In stock"],
            ["outOfStock", pane ? "Out" : "Out of stock"],
          ] as const).map(([key, label]) => {
            const on = key === "all" ? stockTab !== "inStock" && stockTab !== "outOfStock" : stockTab === key;
            return (
              <button
                key={key}
                type="button"
                role="radio"
                aria-checked={on}
                aria-label={key === "all" ? "All stock" : key === "inStock" ? "In stock" : "Out of stock"}
                className={`pl-seg__btn${on ? " is-on" : ""}`}
                onClick={() => {
                  if (key === "all") {
                    onChange(stockTab === "inStock" || stockTab === "outOfStock" ? applyStatusTab(filters, "all") : filters);
                  } else onChange(applyStatusTab(filters, key));
                }}
              >
                {label}
              </button>
            );
          })}
        </div>
        {draftRevisionsEnabled ? (
          <div className="pl-fgrp__row" data-section="edits">
            <span className="pl-grow">
              <b>Live with edits not published</b>
            </span>
            <Switch
              on={filters.hasDraftChanges === true}
              label="Live with edits not published"
              onToggle={() => onChange({ ...filters, hasDraftChanges: !filters.hasDraftChanges })}
            />
          </div>
        ) : null}
      </section>

      <section className="pl-fgrp" data-section="colour" hidden={hide("colour")} aria-label="Colour">
        <H title="Colour" hint={pane ? undefined : "or type any colour"} />
        <div className="pl-chips is-wrap">
          {shownColours.map(([name, hex]) => {
            const on = colour === name.toLowerCase();
            return (
              <button
                key={name}
                type="button"
                aria-pressed={on}
                className={`pl-chip${on ? " is-on" : ""}`}
                onClick={() => onChange({ ...filters, color: on ? "" : name })}
              >
                <i className="pl-cdot" style={{ background: hex }} aria-hidden="true" />
                {name}
              </button>
            );
          })}
          {colour && !shownColours.some(([n]) => n.toLowerCase() === colour) ? (
            <button type="button" aria-pressed className="pl-chip is-on" onClick={() => onChange({ ...filters, color: "" })}>
              {filters.color}
              <PlIcon name="x" size={14} />
            </button>
          ) : null}
          <button type="button" className="pl-chip is-ghost" aria-expanded={moreColours} onClick={() => setMoreColours((v) => !v)}>
            {moreColours ? "Fewer" : "More…"}
          </button>
        </div>
        {moreColours || !pane ? (
          <label className="pl-input">
            <input
              type="text"
              aria-label="Colour"
              placeholder="Any colour, e.g. peacock blue"
              value={filters.color || ""}
              onChange={(event) => onChange({ ...filters, color: event.target.value }, { typing: true })}
            />
          </label>
        ) : null}
      </section>

      <TypeTagFilterSection
        selected={filters.typeTagIds || []}
        onChange={(typeTagIds) => onChange({ ...filters, typeTagIds })}
        review={Boolean(filters.typeTagReview)}
        onReviewChange={(typeTagReview) => onChange({ ...filters, typeTagReview })}
        showTitle={!only}
        hidden={hide("types")}
      />

      <section className="pl-fgrp" data-section="added" hidden={hide("added")} aria-label="Added">
        <H title="Added" />
        <div className={`pl-chips${pane ? " is-wrap" : " is-scroll"}`}>
          {addedPresets.map((preset) => (
            <button
              key={preset.key}
              type="button"
              aria-pressed={added.preset === preset.key}
              className={`pl-chip${added.preset === preset.key ? " is-on" : ""}`}
              onClick={() =>
                onChange({
                  ...filters,
                  added: preset.key === "custom" ? { preset: "custom", from: added.from || "", to: added.to || "" } : { preset: preset.key },
                })
              }
            >
              {pane && preset.key === "custom" ? "Pick" : preset.label}
            </button>
          ))}
        </div>
        {added.preset === "custom" ? (
          <div className="pl-dates">
            <label>
              <span>From</span>
              <input
                type="date"
                value={added.from || ""}
                max={added.to || undefined}
                onChange={(event) => onChange({ ...filters, added: { ...added, preset: "custom", from: event.target.value } })}
              />
            </label>
            <label>
              <span>To</span>
              <input
                type="date"
                value={added.to || ""}
                min={added.from || undefined}
                onChange={(event) => onChange({ ...filters, added: { ...added, preset: "custom", to: event.target.value } })}
              />
            </label>
          </div>
        ) : null}
      </section>

      <div className="pl-fgrp is-rows" hidden={Boolean(only)}>
        {pane ? (
          <Disclosure
            id="craft"
            mode={mode}
            title={`Craft & weave${craftCount ? ` · ${craftCount}` : ""}`}
            hint="artform → weave"
            icon="loom"
            open={Boolean(open.craft)}
            onToggle={() => toggle("craft")}
          >
            <CraftWeavePanel filters={filters} onChange={(next) => onChange(next)} loaders={loaders} />
          </Disclosure>
        ) : (
          <Disclosure
            id="craft"
            mode={mode}
            title={`Craft & weave${craftCount ? ` · ${craftCount}` : ""}`}
            hint="Artform → type → sub-category → weave"
            icon="loom"
            open={false}
            onToggle={() => onOpenCraft?.()}
          />
        )}

        <Disclosure
          id="transferred"
          mode={mode}
          title="Transferred"
          hint={transferred === "transferredIn" ? "Transferred in" : transferred === "transferredOut" ? "Transferred out" : "in / out"}
          icon="swap"
          open={Boolean(open.transferred)}
          onToggle={() => toggle("transferred")}
        >
          <div className="pl-seg" role="radiogroup" aria-label="Transferred">
            {([
              ["", "Any"],
              ["transferredIn", "In"],
              ["transferredOut", "Out"],
            ] as const).map(([view, label]) => (
              <button
                key={view || "any"}
                type="button"
                role="radio"
                aria-checked={transferred === view}
                aria-label={view ? `Transferred ${label.toLowerCase()}` : "Any transfer"}
                className={`pl-seg__btn${transferred === view ? " is-on" : ""}`}
                onClick={() =>
                  // One `view` param: a transferred view replaces Drafts/Live (the tab goes back to All).
                  onChange({ ...filters, view: view || (transferred ? "" : filters.view) })
                }
              >
                {label}
              </button>
            ))}
          </div>
        </Disclosure>

        <Disclosure
          id="ids"
          mode={mode}
          title={`Exact IDs & tags${idsCount ? ` · ${idsCount}` : ""}`}
          hint={pane ? "HKP tag, ID" : "HKP tag, product ID, tags"}
          icon="tag"
          open={Boolean(open.ids)}
          onToggle={() => toggle("ids")}
        >
          {([
            ["xuid", "HKP tag", "e.g. HKP-00418"],
            ["ProductId", "Product ID", "e.g. N10"],
            ["tags", "Tag", "e.g. wedding"],
          ] as const).map(([field, label, placeholder]) => (
            <label key={field} className="pl-field">
              <span>{label}</span>
              <span className="pl-input">
                <input
                  type="text"
                  aria-label={label}
                  placeholder={placeholder}
                  value={(filters[field] as string) || ""}
                  onChange={(event) => onChange({ ...filters, [field]: event.target.value }, { typing: true })}
                />
              </span>
            </label>
          ))}
        </Disclosure>

        <Disclosure
          id="describe"
          mode={mode}
          title="Describe it"
          hint={pane ? "plain words" : "e.g. red saree after 2025 in stock"}
          icon="spark"
          open={Boolean(open.describe)}
          onToggle={() => toggle("describe")}
        >
          <label className="pl-input pl-input--area">
            <textarea
              rows={2}
              aria-label="Describe the products"
              placeholder="e.g. black saree after 2025 in stock"
              value={filters.q || ""}
              onChange={(event) => onChange({ ...filters, q: event.target.value }, { typing: true })}
            />
          </label>
          <div className="pl-fgrp__row">
            <span className="pl-grow">
              <b>Include AI guesses</b>
              <small>Also match colours and types HKP read from photos</small>
            </span>
            <Switch
              on={filters.includeAiSuggestions !== false}
              label="Include AI guesses"
              onToggle={() => onChange({ ...filters, includeAiSuggestions: filters.includeAiSuggestions === false })}
            />
          </div>
        </Disclosure>
      </div>
    </div>
  );
};

export default ProductFiltersPanel;
